import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Chip,
  Autocomplete,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import BaseLayout from "../layout/BaseLayout";
import MultiSelectDropdown from "../component/MultiSelectDropdown";
import { apiService } from "../app/api";
import { toast } from "react-toastify";
import { handleAxiosError } from "../helper/errorHandler";
import { formatTimestamp } from "../helper/formatTimestamp";

interface InstantMapping {
  _id: string;
  src_lang: string;
  tgt_langs: string[];
  createdAt: string;
}

const InstantSpeakerLanguage: React.FC = () => {
  const [mappings, setMappings] = useState<InstantMapping[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");

  // Add Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSrc, setNewSrc] = useState("");
  const [newTgt, setNewTgt] = useState<string[]>([]);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editSrc, setEditSrc] = useState("");
  const [editTgt, setEditTgt] = useState<string[]>([]);

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch All
  const fetchMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiService.get("/instant-speaker-languages");
      setMappings(data);
    } catch (err: any) {
      setError("Failed to fetch mappings.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Languages
  const fetchLanguages = async () => {
    try {
      const { data } = await apiService.get(
        "/languages?is_active=true&page=1&limit=500&fields=language"
      );
      setLanguages(data.data.map((l: any) => l.language));
    } catch (err) {
      handleAxiosError(err);
    }
  };

  useEffect(() => {
    fetchMappings();
    fetchLanguages();
  }, []);

  const filteredMappings = mappings.filter((m) =>
    m.src_lang.toLowerCase().includes(search.toLowerCase())
  );

  // CREATE
  const handleCreate = async () => {
    if (!newSrc.trim()) return toast.warning("Source language required.");
    if (newTgt.length === 0)
      return toast.warning("Select at least one target language.");

    try {
      await apiService.post("/instant-speaker-language", {
        src_lang: newSrc,
        tgt_langs: newTgt,
      });

      toast.success("Mapping created.");
      setAddDialogOpen(false);
      setNewSrc("");
      setNewTgt([]);
      fetchMappings();
    } catch (err) {
      handleAxiosError(err);
    }
  };

  // EDIT
  const openEdit = (m: InstantMapping) => {
    setEditId(m._id);
    setEditSrc(m.src_lang);
    setEditTgt(m.tgt_langs);
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    if (!editId) return;

    try {
      await apiService.put(`/instant-speaker-language/${editId}`, {
        src_lang: editSrc,
        tgt_langs: editTgt,
      });

      toast.success("Updated successfully.");
      setEditDialogOpen(false);
      setEditId(null);
      fetchMappings();
    } catch (err) {
      handleAxiosError(err);
    }
  };

  // DELETE
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await apiService.delete(`/instant-speaker-language/${deleteId}`);
      toast.success("Deleted successfully.");

      setDeleteDialogOpen(false);
      fetchMappings();
    } catch (err) {
      handleAxiosError(err);
    }
  };

  return (
    <BaseLayout>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" pt={2}>
        <Typography fontSize={30} fontWeight={700}>
          Instant Speaker Language ({filteredMappings.length})
        </Typography>

        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search by source language..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: "300px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
            Add Mapping
          </Button>
        </Box>
      </Box>

      {/* LOADER */}
      {loading && (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {/* ERROR */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* LIST */}
      {!loading && !error && (
        <Grid container spacing={3} mt={2}>
          {filteredMappings.length > 0 ? (
            filteredMappings.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m._id}>
                <Card sx={{ position: "relative", p: 1 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600}>
                      {m.src_lang}
                    </Typography>

                    <Typography variant="body2" mt={1} fontWeight={600}>
                      Target Languages:
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {m.tgt_langs.map((t, idx) => (
                        <Chip key={idx} label={t} size="small" />
                      ))}
                    </Box>

                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Added on: {formatTimestamp(m.createdAt)}
                    </Typography>

                    <IconButton
                      sx={{ position: "absolute", top: 8, right: 40 }}
                      onClick={() => openEdit(m)}
                    >
                      <EditIcon color="primary" />
                    </IconButton>

                    <IconButton
                      sx={{ position: "absolute", top: 8, right: 8 }}
                      onClick={() => {
                        setDeleteId(m._id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography mt={5} textAlign="center" width="100%">
              No mappings found.
            </Typography>
          )}
        </Grid>
      )}

      {/* ADD DIALOG */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add Instant Speaker Mapping</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={languages.sort()}
            value={newSrc}
            onChange={(_, value) => setNewSrc(value || "")}
            renderInput={(params) => (
              <TextField {...params} label="Source Language" />
            )}
            sx={{ mt: 1, mb: 2, minWidth: 450 }}
          />

          <MultiSelectDropdown
            options={languages.sort()}
            value={newTgt}
            label="Target Languages"
            placeholder="Select target languages"
            onChange={(val) => setNewTgt(val)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Mapping</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Source Language"
            value={editSrc}
            disabled
            sx={{ mt: 1, mb: 2, minWidth: 450 }}
          />

          <MultiSelectDropdown
            options={languages.sort()}
            value={editTgt}
            label="Target Languages"
            placeholder="Select target languages"
            onChange={(val) => setEditTgt(val)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Mapping</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this mapping?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </BaseLayout>
  );
};

export default InstantSpeakerLanguage;
